import React, { useEffect, useMemo, useState } from 'react';
import { sdk } from '../../lib/sdk';
import { getErrorMessage } from '../../lib/utils/errorMessage';

type BackupSchedule = {
  id?: string;
  _id?: string;
  frequencyType: 'daily' | 'weekly' | 'cron';
  timezone: string;
  hour?: number;
  minute?: number;
  weekdays?: number[];
  cronExpression?: string;
  recipientEmails: string[];
  isActive: boolean;
  nextRunAt?: string;
  retentionPolicy?: {
    keepDays: number;
    maxRuns?: number;
  };
};

type BackupRun = {
  _id: string;
  status: 'running' | 'success' | 'failed';
  startedAt?: string;
  endedAt?: string;
  errorDetails?: {
    message?: string;
  };
};

const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const resolveErrorMessage = (error: any, fallback: string): string => {
  const code = error?.response?.data?.code;
  if (code === 'ROUTE_NOT_FOUND') {
    return 'Automated backups API is not available in this environment yet.';
  }
  return getErrorMessage(error, fallback);
};

const toTimeString = (hour: number, minute: number): string => {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const parseTimeString = (value: string): { hour: number; minute: number } => {
  const [rawHour = '0', rawMinute = '0'] = value.split(':');
  const nextHour = Number(rawHour);
  const nextMinute = Number(rawMinute);
  return {
    hour: Number.isFinite(nextHour) ? Math.min(Math.max(nextHour, 0), 23) : 0,
    minute: Number.isFinite(nextMinute) ? Math.min(Math.max(nextMinute, 0), 59) : 0,
  };
};

const normalizeSchedule = (schedule: any): BackupSchedule => ({
  ...schedule,
  id: schedule?.id || schedule?._id,
});

const extractList = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const AutomatedBackups: React.FC = () => {
  const [schedules, setSchedules] = useState<BackupSchedule[]>([]);
  const [history, setHistory] = useState<BackupRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [frequencyType, setFrequencyType] = useState<'daily' | 'weekly' | 'cron'>('daily');
  const [timezone, setTimezone] = useState('Asia/Karachi');
  const [hour, setHour] = useState(2);
  const [minute, setMinute] = useState(0);
  const [weekdays, setWeekdays] = useState<number[]>([0]);
  const [cronExpression, setCronExpression] = useState('0 2 * * *');
  const [keepDays, setKeepDays] = useState(7);
  const [maxRuns, setMaxRuns] = useState(20);
  const [recipientInput, setRecipientInput] = useState('');
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);

  const sortedSchedules = useMemo(
    () => [...schedules].sort((a, b) => new Date(a.nextRunAt || 0).getTime() - new Date(b.nextRunAt || 0).getTime()),
    [schedules]
  );

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [scheduleResponse, historyResponse] = await Promise.all([
        sdk.client.get('/api/v1/automation/backups/schedules'),
        sdk.client.get('/api/v1/automation/backups/history?limit=10'),
      ]);
      setSchedules(extractList(scheduleResponse).map((item: any) => normalizeSchedule(item)));
      setHistory(extractList(historyResponse));
    } catch (e: any) {
      setError(resolveErrorMessage(e, 'Failed to load automated backup data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const addRecipient = () => {
    const email = recipientInput.trim().toLowerCase();
    if (!email || recipientEmails.includes(email)) {
      setRecipientInput('');
      return;
    }
    setRecipientEmails((prev) => [...prev, email]);
    setRecipientInput('');
  };

  const toggleWeekday = (day: number) => {
    setWeekdays((prev) => {
      const exists = prev.includes(day);
      const next = exists ? prev.filter((value) => value !== day) : [...prev, day].sort();
      return next.length ? next : [0];
    });
  };

  const createSchedule = async () => {
    if (!recipientEmails.length) {
      setError('Add at least one recipient email');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await sdk.client.post('/api/v1/automation/backups/schedules', {
        frequencyType,
        timezone,
        hour: frequencyType === 'cron' ? undefined : hour,
        minute: frequencyType === 'cron' ? undefined : minute,
        weekdays: frequencyType === 'weekly' ? weekdays : undefined,
        cronExpression: frequencyType === 'cron' ? cronExpression : undefined,
        recipientEmails,
        retentionPolicy: {
          keepDays,
          maxRuns,
        },
      });
      const createdPayload = response?.data ?? response;
      const created = createdPayload ? normalizeSchedule(createdPayload) : null;
      setRecipientEmails([]);
      if (created?.id) {
        setSchedules((prev) => [created, ...prev.filter((item) => (item.id || item._id) !== created.id)]);
      }
      await loadData();
    } catch (e: any) {
      setError(resolveErrorMessage(e, 'Failed to create backup schedule'));
      setLoading(false);
    }
  };

  const runNow = async (id: string) => {
    setLoading(true);
    try {
      await sdk.client.post(`/api/v1/automation/backups/schedules/${id}/run-now`, {});
      await loadData();
    } catch (e: any) {
      setError(resolveErrorMessage(e, 'Failed to run backup schedule'));
      setLoading(false);
    }
  };

  const toggleScheduleState = async (id: string, isActive: boolean) => {
    setLoading(true);
    try {
      if (isActive) {
        await sdk.client.post(`/api/v1/automation/backups/schedules/${id}/pause`, {});
      } else {
        await sdk.client.post(`/api/v1/automation/backups/schedules/${id}/resume`, {});
      }
      await loadData();
    } catch (e: any) {
      setError(resolveErrorMessage(e, 'Failed to update schedule status'));
      setLoading(false);
    }
  };

  const deleteSchedule = async (id: string) => {
    setLoading(true);
    try {
      await sdk.client.delete(`/api/v1/automation/backups/schedules/${id}`);
      await loadData();
    } catch (e: any) {
      setError(resolveErrorMessage(e, 'Failed to delete backup schedule'));
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Automated Backups</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Configure recurring database backups, retention rules, and recipient delivery.
        </p>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="card space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New Backup Schedule</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="form-label">Frequency</label>
              <select className="form-select" value={frequencyType} onChange={(e) => setFrequencyType(e.target.value as any)}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="cron">Cron</option>
              </select>
            </div>
            <div>
              <label className="form-label">Timezone</label>
              <input className="form-input" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
            </div>
          </div>

          {frequencyType === 'cron' ? (
            <div>
              <label className="form-label">Cron expression</label>
              <input className="form-input" value={cronExpression} onChange={(e) => setCronExpression(e.target.value)} />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="form-label">Scheduled time</label>
                <input
                  className="form-input"
                  type="time"
                  value={toTimeString(hour, minute)}
                  onChange={(e) => {
                    const parsed = parseTimeString(e.target.value);
                    setHour(parsed.hour);
                    setMinute(parsed.minute);
                  }}
                />
              </div>
              <div />
            </div>
          )}

          {frequencyType === 'weekly' ? (
            <div>
              <label className="form-label">Weekdays</label>
              <div className="flex flex-wrap gap-2">
                {weekdayLabels.map((label, index) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => toggleWeekday(index)}
                    className={`px-2 py-1 rounded text-xs ${
                      weekdays.includes(index)
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="form-label">Keep days</label>
              <input
                className="form-input"
                type="number"
                min={1}
                value={keepDays}
                onChange={(e) => setKeepDays(Number(e.target.value))}
              />
            </div>
            <div>
              <label className="form-label">Max backup runs</label>
              <input
                className="form-input"
                type="number"
                min={1}
                value={maxRuns}
                onChange={(e) => setMaxRuns(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="form-label">Recipients</label>
            <div className="flex gap-2">
              <input
                className="form-input flex-1"
                value={recipientInput}
                placeholder="recipient@example.com"
                onChange={(e) => setRecipientInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addRecipient();
                  }
                }}
              />
              <button className="btn btn-primary px-3 py-2" type="button" onClick={addRecipient}>
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {recipientEmails.map((email) => (
                <button
                  key={email}
                  type="button"
                  className="px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-xs"
                  onClick={() => setRecipientEmails((prev) => prev.filter((item) => item !== email))}
                >
                  {email} x
                </button>
              ))}
            </div>
          </div>

          <button className="btn btn-primary px-4 py-2" type="button" disabled={loading} onClick={createSchedule}>
            Create Backup Schedule
          </button>
        </div>

        <div className="card space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Existing Schedules</h3>
          {loading ? <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p> : null}
          {!loading && !sortedSchedules.length ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No backup schedules configured yet.</p>
          ) : null}
          <div className="space-y-3 max-h-[380px] overflow-auto pr-1">
            {sortedSchedules.map((schedule) => {
              const scheduleId = schedule.id || schedule._id;
              if (!scheduleId) return null;
              return (
              <div key={scheduleId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {schedule.frequencyType.toUpperCase()} - {schedule.timezone}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Next run: {schedule.nextRunAt ? new Date(schedule.nextRunAt).toLocaleString() : 'N/A'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Recipients: {schedule.recipientEmails.join(', ') || 'None'}
                </p>
                <div className="mt-2 flex gap-2">
                  <button className="btn btn-secondary px-3 py-1.5" type="button" onClick={() => runNow(scheduleId)}>
                    Run Now
                  </button>
                  <button
                    className="btn btn-secondary px-3 py-1.5"
                    type="button"
                    onClick={() => toggleScheduleState(scheduleId, schedule.isActive)}
                  >
                    {schedule.isActive ? 'Pause' : 'Resume'}
                  </button>
                  <button className="btn btn-secondary px-3 py-1.5" type="button" onClick={() => deleteSchedule(scheduleId)}>
                    Delete
                  </button>
                </div>
              </div>
            )})}
          </div>

          <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100">Recent Backup Runs</h4>
          <div className="space-y-2 max-h-[220px] overflow-auto pr-1">
            {!history.length ? <p className="text-sm text-gray-500 dark:text-gray-400">No backup runs yet.</p> : null}
            {history.map((run) => (
              <div key={run._id} className="border border-gray-200 dark:border-gray-700 rounded p-2">
                <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                  {run.status.toUpperCase()} - {run.startedAt ? new Date(run.startedAt).toLocaleString() : 'N/A'}
                </p>
                {run.errorDetails?.message ? (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">{run.errorDetails.message}</p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomatedBackups;
